import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { WhyChooseUsSection as WhyChooseUsType, Stat } from '../../../types';

interface WhyChooseUsProps {
  data?: WhyChooseUsType;
  fallbackFeatures?: Array<{
    icon: string;
    title: string;
    description: string;
    id?: string | number;
    color?: string;
  }>;
}

// Fallback features data when API response is null or invalid
const DEFAULT_WHY_CHOOSE_US_DATA: WhyChooseUsType = {
  title: 'Tại sao nên học tiếng Anh tại',
  titlePrefix: 'Tại sao nên học tiếng Anh tại',
  brandName: 'Anh ngữ Jennifer',
  brandColor: '#dc2626',
  cardBg: '#013d35',
  features: [
    {
      icon: 'graduation',
      title: 'Đội ngũ giảng viên trình độ cao',
      description:
        'Đội ngũ Giáo viên được đào tạo bài bản từ các trường Đại học dạy ngoại ngữ hàng đầu trong và ngoài nước và Có kinh nghiệm ôn luyện IELT, AEF..., có thể cung cấp cho học viên những kiến thức và kỹ năng cần thiết để đạt được mục tiêu học tập của mình.',
    },
    {
      icon: 'book',
      title: 'Giáo trình thiết kế độc quyền',
      description:
        'Giáo trình được thiết kế riêng cho từng cấp độ và mục tiêu, kết hợp phương pháp giảng dạy hiện đại với nội dung thực tế, giúp học viên tiến bộ nhanh chóng và hiệu quả.',
    },
    {
      icon: 'chart',
      title: 'Quản lý chất lượng',
      description:
        'Hệ thống theo dõi và đánh giá tiến độ học tập minh bạch, giúp học viên và phụ huynh nắm bắt kết quả rõ ràng. Đội ngũ quản lý chất lượng liên tục cải tiến chương trình giảng dạy.',
    },
    {
      icon: 'laptop',
      title: 'Học trực tuyến linh hoạt',
      description:
        'Bạn có thể học mọi lúc, mọi nơi trên tất cả các thiết bị. Hệ thống e-learning hiện đại với video bài giảng, bài tập tương tác và kiểm tra trực tuyến.',
    },
    {
      icon: 'comments',
      title: 'Hỗ trợ tận tâm 24/7',
      description:
        'Đội ngũ hỗ trợ luôn sẵn sàng giải đáp mọi thắc mắc. Học viên được hỗ trợ trực tiếp từ giáo viên và trợ giảng trong suốt quá trình học.',
    },
    {
      icon: 'globe',
      title: 'Môi trường học quốc tế',
      description:
        'Học tập trong môi trường đa văn hóa, giao tiếp với giáo viên và học viên từ nhiều quốc gia, giúp nâng cao kỹ năng nghe nói tự nhiên và tự tin.',
    },
  ],
  stats: [
    { value: '8.500+', label: 'Học viên đạt kết quả như ý' },
    { value: '20+', label: 'Chương trình thiết kế riêng' },
    { value: '50+', label: 'Giảng viên trình độ cao' },
    { value: '3+', label: 'Cơ sở khang trang tại Hà Nội' },
  ],
};

const iconBgMap: { [key: number]: string } = {
  0: 'bg-teal-100 text-teal-600',
  1: 'bg-emerald-100 text-emerald-600',
  2: 'bg-green-100 text-green-600',
  3: 'bg-amber-100 text-amber-500',
  4: 'bg-cyan-100 text-cyan-600',
  5: 'bg-lime-100 text-lime-600',
};

const FeatureIcon: React.FC<{ icon: string; className?: string }> = ({ icon, className = 'w-10 h-10' }) => (
  <span className={className} dangerouslySetInnerHTML={{ __html: icon }} />
);

const WhyChooseUs: React.FC<WhyChooseUsProps> = ({ data, fallbackFeatures }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: false, amount: 0.2 });
  const chooseUsData = data || DEFAULT_WHY_CHOOSE_US_DATA;

  const features = chooseUsData.features || [];
  const activeFeature = features[activeIndex];
  const titlePrefix = chooseUsData.titlePrefix || chooseUsData.title || 'Tại sao nên học tiếng Anh tại';
  const brandName = chooseUsData.brandName || 'Anh ngữ Jennifer';
  const brandColor = chooseUsData.brandColor || '#dc2626';
  const cardBg = chooseUsData.cardBg || '#013d35';
  const stats: Stat[] = chooseUsData.stats || [];

  return (
    <section
      ref={sectionRef}
      className="py-16 bg-white dark:bg-gray-900"
      id="why-choose-us"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Title */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 dark:text-gray-100 mb-10"
        >
          {titlePrefix}{' '}
          <span style={{ color: brandColor }}>{brandName}</span>
        </motion.h2>

        {/* Dark card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="rounded-3xl p-5 sm:p-8 mb-6"
          style={{ backgroundColor: cardBg }}
        >
          {/* Feature tab grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
            {features.map((feature, index) => (
              <motion.button
                key={index}
                onClick={() => setActiveIndex(index)}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className={`rounded-2xl p-4 sm:p-5 flex flex-col items-center text-center gap-2.5 transition-all duration-300 ${
                  index === activeIndex
                    ? 'bg-emerald-100 shadow-lg ring-2 ring-[#017e6e]'
                    : 'bg-white hover:bg-emerald-50'
                }`}
              >
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBgMap[index % 6]}`}>
                  <FeatureIcon icon={feature.icon || ''} />
                </div>
                <span className="text-sm font-semibold text-gray-800 leading-snug line-clamp-2">
                  {feature.title}
                </span>
              </motion.button>
            ))}
          </div>

          {/* Active detail panel */}
          {activeFeature && (
            <motion.div
              key={activeIndex}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
              className="text-center px-2 sm:px-6 py-2"
            >
              <h3 className="text-base sm:text-lg font-bold text-white mb-2">
                {activeFeature.title}
              </h3>
              <p className="text-emerald-100 text-sm sm:text-base leading-relaxed max-w-2xl mx-auto">
                {activeFeature.description}
              </p>
            </motion.div>
          )}
        </motion.div>

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4"
        >
          {stats.map((stat, i) => (
            <motion.div
              key={i}
              className="bg-emerald-50 dark:bg-gray-800 rounded-2xl p-5 sm:p-6 text-center"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <div className="text-3xl sm:text-4xl font-bold text-[#017e6e] dark:text-emerald-400 mb-1">
                {stat.value}
              </div>
              <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-snug">
                {stat.label}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
